/**
 * Reviews over Yotpo's public widget APIs — three elements sharing one list engine:
 * - <home-reviews> (sections/home-reviews.liquid): the store's site reviews with Yotpo's own
 *   bottomline (average, count, star distribution) from the widget endpoint.
 * - <reviews-page> (sections/main-reviews.liquid, /pages/reviews): every review — site and
 *   product — through the filter endpoint: tabs (all / with photos / about the store), free
 *   text search, star filter, sort; photos with a lightbox; the reviewed product. The summary
 *   is built from one tiny per-star count request each (Yotpo has no all-reviews bottomline).
 *   Tab / star / sort / query live in the URL (replaceState) so a filtered view can be shared.
 * - <product-reviews> (sections/product-reviews.liquid): one product's reviews with Yotpo's
 *   bottomline and its sub-ratings (custom rating fields), on the product page.
 * Shared: near-viewport start, "more" paging up to data-max, sessionStorage cache (1h) per
 * request, relative Hebrew dates, initial avatars, 4-line clamp with an expand toggle, one
 * AbortController for fetches, aborted on disconnect.
 */
if (!customElements.get('home-reviews')) {
  const WIDGET_API = 'https://api-cdn.yotpo.com/v1/widget';
  const FILTER_API = 'https://api-cdn.yotpo.com/v1/reviews';
  const CACHE_PREFIX = 'reviews:v1:';
  const CACHE_MS = 60 * 60 * 1000;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const CLAMP_CHARS = 180;
  const SEARCH_DEBOUNCE = 350;
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const fill = (text, values) => (text || '').replace(/\[(\w+)\]/g, (_, key) => (key in values ? values[key] : `[${key}]`));
  const he = (n) => Number(n).toLocaleString('he-IL');

  class ReviewsList extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key;
      this.perPage = Math.max(1, parseInt(this.dataset.per, 10) || 4);
      this.max = Math.max(this.perPage, parseInt(this.dataset.max, 10) || 20);
      this.sort = this.dataset.sort || 'date';
      this.star = 0; // exact-star filter from the distribution bars
      this.labels = JSON.parse(this.querySelector('[data-hr-labels]')?.textContent || '{}');
      this.template = this.querySelector('[data-hr-item]');
      this.list = this.querySelector('[data-hr-list]');
      this.more = this.querySelector('[data-hr-more]');
      this.sortSelect = this.querySelector('[data-hr-sort]');
      this.lightbox = this.querySelector('[data-rp-lightbox]');
      this.page = 0;
      this.total = 0;
      this.loaded = 0;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      if (!this.key || !this.template || !this.list) return;
      this.init?.(signal);

      this.more?.addEventListener('click', () => this.load(this.page + 1), { signal });
      this.sortSelect?.addEventListener('change', () => {
        this.sort = this.sortSelect.value;
        this.sync?.();
        this.reset();
        this.load(1);
      }, { signal });
      this.querySelector('[data-hr-dist]')?.addEventListener('click', (event) => {
        const row = event.target.closest('[data-star]');
        if (!row) return;
        this.setStar(this.star === Number(row.dataset.star) ? 0 : Number(row.dataset.star));
      }, { signal });
      this.querySelector('[data-hr-filter-clear]')?.addEventListener('click', () => this.setStar(0), { signal });
      this.list.addEventListener('click', (event) => {
        const photo = event.target.closest('[data-photo]');
        if (photo) return this.openPhoto(photo);
        const button = event.target.closest('[data-expand]');
        if (!button) return;
        const text = button.previousElementSibling;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        text.classList.toggle('is-clamped', !open);
        button.textContent = open ? this.labels.less : this.labels.more;
      }, { signal });

      this.lightbox?.addEventListener('click', (event) => {
        if (event.target === this.lightbox || event.target.closest('[data-rp-lightbox-close]')) this.lightbox.close();
      }, { signal });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.start();
        }, { rootMargin: '600px 0px' });
        this.observer.observe(this);
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.wallObserver?.disconnect();
      clearTimeout(this.searchTimer);
    }

    start() {
      this.load(1);
    }

    reset() {
      this.page = 0;
      this.loaded = 0;
      this.list.replaceChildren();
      this.more.hidden = true;
      this.querySelector('[data-hr-empty]').hidden = true;
    }

    /** Filters the list to one star rating (0 = all) and reflects it on the bars and the chip. */
    setStar(star, { load = true } = {}) {
      this.star = star;
      this.querySelectorAll('[data-star]').forEach((row) => row.setAttribute('aria-pressed', String(Number(row.dataset.star) === star)));
      const chip = this.querySelector('[data-hr-filter]');
      if (chip) {
        chip.hidden = !star;
        chip.querySelector('[data-hr-filter-text]').textContent = star === 1 ? this.labels.filteredOne : fill(this.labels.filtered, { n: star });
      }
      if (!load) return;
      this.sync?.();
      this.reset();
      this.load(1);
      if (star) this.list.closest('[data-hr-main]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    async load(page) {
      if (this.loading) return;
      this.loading = true;
      this.more.disabled = true;
      this.list.setAttribute('aria-busy', 'true');
      const request = ++this.requestId || (this.requestId = 1);
      try {
        const data = await this.fetchPage(page);
        if (request !== this.requestId) return; // a newer filter superseded this reply
        if (page === 1) this.list.replaceChildren();
        this.page = page;
        this.total = data.pagination?.total ?? data.reviews.length;
        if (data.bottomline && !this.bottomlineDone) this.renderBottomline(data.bottomline);
        this.renderReviews(data.reviews, data);
        this.loaded += data.reviews.length;
        const empty = this.querySelector('[data-hr-empty]');
        empty.hidden = this.loaded > 0;
        if (!this.loaded) empty.textContent = this.labels.empty;
        const remaining = Math.min(this.total, this.max) - this.loaded;
        this.more.hidden = remaining <= 0 || data.reviews.length < this.perPage;
        this.more.disabled = false;
        this.afterLoad?.(data);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.list.replaceChildren();
        this.querySelector('[data-hr-error]').hidden = false;
        this.more.hidden = true;
      } finally {
        this.list.setAttribute('aria-busy', 'false');
        this.loading = false;
      }
    }

    /** Site reviews from the widget endpoint (bottomline included). */
    async fetchPage(page) {
      const params = new URLSearchParams({ per_page: this.perPage, page, sort: this.sort, direction: 'desc' });
      if (this.star) params.set('star', this.star);
      return this.request(`${WIDGET_API}/${this.key}/products/yotpo_site_reviews/reviews.json?${params}`);
    }

    /** GET or POST JSON, with a one-hour sessionStorage cache per request. */
    async request(url, body) {
      const cacheKey = CACHE_PREFIX + url + (body ? JSON.stringify(body) : '');
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.at < CACHE_MS) return cached.data;
      } catch (error) {
        // storage blocked: fetch every time
      }
      const init = { signal: this.abortController.signal };
      if (body) Object.assign(init, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const response = await fetch(url, init);
      if (!response.ok) throw new Error(`Yotpo ${response.status}`);
      const payload = await response.json();
      const data = payload?.response;
      if (!data?.reviews) throw new Error('Yotpo: no reviews');
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data }));
      } catch (error) {
        // quota or private mode: fine
      }
      return data;
    }

    renderBottomline(bottomline) {
      if (!bottomline) return;
      this.bottomlineDone = true;
      const avg = Number(bottomline.average_score) || 0;
      const total = Number(bottomline.total_review) || this.total;
      const avgText = (Math.round(avg * 10) / 10).toFixed(1);
      const avgEl = this.querySelector('[data-hr-avg]');
      if (avgEl && avg) avgEl.textContent = avgText;
      const stars = this.querySelector('[data-hr-stars]');
      if (stars) {
        this.paintStars(stars, avg);
        stars.setAttribute('aria-label', fill(this.labels.ratingOf, { avg: avgText }));
      }
      const countEl = this.querySelector('[data-hr-count]');
      if (countEl && total) countEl.textContent = fill(this.labels.verifiedCount, { n: he(total) });
      this.renderTotal(total);
      this.renderDistribution(bottomline.star_distribution, total);
    }

    renderTotal(total) {
      const totalEl = this.querySelector('[data-hr-total]');
      if (totalEl && total) totalEl.textContent = fill(this.labels.basedOn, { n: he(total) });
    }

    /** The 5→1 bars: share of reviews per star. */
    renderDistribution(distribution, total) {
      if (!distribution || !total) return;
      this.querySelectorAll('[data-star]').forEach((row) => {
        const count = Number(distribution[row.dataset.star]) || 0;
        const pct = Math.round((count / total) * 100);
        row.querySelector('[data-fill]').style.width = `${count ? Math.max(pct, 1.5) : 0}%`;
        row.querySelector('[data-pct]').textContent = `${pct}%`;
        row.setAttribute('aria-label', `${row.dataset.star} כוכבים: ${he(count)} ביקורות (${pct}%)`);
      });
    }

    renderReviews(reviews, data) {
      const fragment = document.createDocumentFragment();
      reviews.forEach((review, index) => {
        const node = this.template.content.firstElementChild.cloneNode(true);
        node.style.animationDelay = `${Math.min(index, 6) * 40}ms`;

        const stars = node.querySelector('[data-stars]');
        stars.replaceChildren(...Array.from({ length: 5 }, () => this.starSvg()));
        this.paintStars(stars, Number(review.score) || 0);
        stars.setAttribute('aria-label', fill(this.labels.ratingOf, { avg: String(review.score) }));

        const time = node.querySelector('[data-date]');
        const created = new Date(review.created_at);
        if (!Number.isNaN(created.getTime())) {
          time.dateTime = created.toISOString().slice(0, 10);
          time.textContent = this.relative(created);
          time.title = created.toLocaleDateString('he-IL');
        }

        const content = (review.content || '').trim();
        const title = (review.title || '').trim();
        const text = content || title;
        const showTitle = title && content && title !== content && !content.startsWith(title);
        const titleEl = node.querySelector('[data-title]');
        if (showTitle) { titleEl.textContent = title; titleEl.hidden = false; }
        const textEl = node.querySelector('[data-text]');
        textEl.textContent = text;
        if (text.length > CLAMP_CHARS) {
          textEl.classList.add('is-clamped');
          const expand = node.querySelector('[data-expand]');
          expand.hidden = false;
          expand.textContent = this.labels.more;
        }

        const name = (review.user?.display_name || '').trim() || 'לקוח/ה';
        node.querySelector('[data-name]').textContent = name;
        const avatar = node.querySelector('[data-avatar]');
        avatar.textContent = name.charAt(0);
        if (review.verified_buyer) {
          avatar.classList.add('is-verified');
          node.querySelector('[data-verified]').hidden = false;
        }
        this.decorate?.(node, review, data);
        fragment.append(node);
      });
      this.list.append(fragment);
    }

    /** Review photos as thumbnails that open the lightbox. */
    renderPhotos(node, review) {
      const photos = node.querySelector('[data-photos]');
      const images = (review.images_data || []).filter((image) => image.thumb_url || image.image_url);
      if (!photos || !images.length) return;
      photos.hidden = false;
      images.slice(0, 6).forEach((image, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rp__photo';
        button.dataset.photo = image.image_url || image.thumb_url;
        button.setAttribute('aria-label', fill(this.labels.photoN, { n: index + 1 }));
        const img = document.createElement('img');
        img.src = image.thumb_url || image.image_url;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 72;
        img.height = 72;
        button.append(img);
        photos.append(button);
      });
    }

    openPhoto(button) {
      if (!this.lightbox) return;
      this.lightbox.querySelector('img').src = button.dataset.photo;
      this.lightbox.showModal();
    }

    starSvg() {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'hr__star');
      svg.setAttribute('aria-hidden', 'true');
      const use = document.createElementNS(SVG_NS, 'use');
      use.setAttribute('href', '#hr-star');
      svg.append(use);
      return svg;
    }

    /** Lights the stars for a score; a fractional score lights the next star at half. */
    paintStars(container, score) {
      container.querySelectorAll('.hr__star').forEach((star, index) => {
        star.classList.toggle('is-on', score >= index + 0.75);
        star.classList.toggle('is-half', score < index + 0.75 && score >= index + 0.25);
      });
      if (!document.getElementById('hr-half')) {
        const svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('hidden', '');
        svg.setAttribute('aria-hidden', 'true');
        svg.innerHTML = '<defs><linearGradient id="hr-half"><stop offset="50%" stop-color="var(--hr-star)"/><stop offset="50%" stop-color="var(--hr-star-empty)"/></linearGradient></defs>';
        this.prepend(svg);
      }
    }

    relative(date) {
      const L = this.labels;
      const days = Math.floor((Date.now() - date.getTime()) / DAY_MS);
      if (days <= 0) return L.today;
      if (days === 1) return L.yesterday;
      if (days === 2) return L.twoDays;
      if (days < 7) return fill(L.days, { n: days });
      const weeks = Math.floor(days / 7);
      if (days < 30) return weeks === 1 ? L.week : weeks === 2 ? L.twoWeeks : fill(L.weeks, { n: weeks });
      const months = Math.floor(days / 30);
      if (days < 365) return months === 1 ? L.month : months === 2 ? L.twoMonths : fill(L.months, { n: months });
      const years = Math.floor(days / 365);
      return years === 1 ? L.year : years === 2 ? L.twoYears : fill(L.years, { n: years });
    }
  }

  customElements.define('home-reviews', ReviewsList);

  /* ---------------- /pages/reviews ---------------- */
  const TABS = ['all', 'photos', 'site'];
  const SORTS = { date: 'date', rating: 'score', votes_up: 'votes_up' };

  class ReviewsPage extends ReviewsList {
    init(signal) {
      this.tab = 'all';
      this.query = '';
      this.products = new Map();
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.search = this.querySelector('[data-rp-search]');

      // URL → state (shareable filtered views)
      const params = new URLSearchParams(location.search);
      if (TABS.includes(params.get('tab'))) this.tab = params.get('tab');
      if (params.get('sort') in SORTS) this.sort = params.get('sort');
      if (this.sortSelect) this.sortSelect.value = this.sort;
      this.query = (params.get('q') || '').trim().slice(0, 80);
      if (this.search) this.search.value = this.query;
      const star = parseInt(params.get('star'), 10);
      if (star >= 1 && star <= 5) this.setStar(star, { load: false });
      this.paintTabs();

      this.tabs.forEach((tab) => tab.addEventListener('click', () => {
        if (tab.dataset.tab === this.tab) return;
        this.tab = tab.dataset.tab;
        this.paintTabs();
        this.sync();
        this.reset();
        this.load(1);
      }, { signal }));
      this.search?.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          const query = this.search.value.trim().slice(0, 80);
          if (query === this.query || (query.length === 1)) return;
          this.query = query;
          this.sync();
          this.reset();
          this.load(1);
        }, SEARCH_DEBOUNCE);
      }, { signal });
      this.search?.closest('form')?.addEventListener('submit', (event) => event.preventDefault(), { signal });
      this.querySelector('[data-rp-wall-more]')?.addEventListener('click', (event) => {
        this.querySelector('[data-rp-wall]')?.classList.remove('is-collapsed');
        event.currentTarget.hidden = true;
        this.revealWall();
      }, { signal });
      this.revealWall();
    }

    /** Curated cards rise in as they enter the viewport, staggered by their column position. */
    revealWall() {
      const wall = this.querySelector('[data-rp-wall]');
      if (!wall || !('IntersectionObserver' in window)) return;
      wall.dataset.reveal = '';
      this.wallObserver?.disconnect();
      const cards = [...wall.querySelectorAll('.rp__card:not(.is-in)')].filter((card) => getComputedStyle(card).display !== 'none');
      if (!cards.length) return;
      cards.forEach((card, index) => card.style.setProperty('--i', String(index % 4)));
      this.wallObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          this.wallObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.15 });
      cards.forEach((card) => this.wallObserver.observe(card));
    }

    start() {
      this.loadSummary();
      this.load(1);
    }

    paintTabs() {
      this.tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.tab === this.tab)));
    }

    sync() {
      const url = new URL(location.href);
      const set = (key, value) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
      set('tab', this.tab === 'all' ? '' : this.tab);
      set('star', this.star ? String(this.star) : '');
      set('sort', this.sort === 'date' ? '' : this.sort);
      set('q', this.query);
      history.replaceState(history.state, '', url);
    }

    body(extra) {
      const body = { page: 1, per_page: this.perPage, sortings: [{ sort_by: SORTS[this.sort] || 'date', ascending: false }], ...extra };
      if (this.star) body.scores = [this.star];
      if (this.tab === 'photos') body.pictured = true;
      if (this.tab === 'site') body.domain_key = 'yotpo_site_reviews';
      if (this.query) body.free_text_search = this.query;
      return body;
    }

    async fetchPage(page) {
      const data = await this.request(`${FILTER_API}/${this.key}/filter.json`, this.body({ page }));
      (data.products || []).forEach((product) => this.products.set(String(product.id), product));
      return data;
    }

    /** No all-reviews bottomline exists, so the summary is one count per star (cached). */
    async loadSummary() {
      try {
        const counts = await Promise.all([5, 4, 3, 2, 1].map(async (star) => {
          const data = await this.request(`${FILTER_API}/${this.key}/filter.json`, { page: 1, per_page: 1, scores: [star] });
          return [star, Number(data.pagination?.total) || 0];
        }));
        const total = counts.reduce((sum, [, n]) => sum + n, 0);
        if (!total) return;
        const average = counts.reduce((sum, [star, n]) => sum + star * n, 0) / total;
        this.renderBottomline({ average_score: average, total_review: total, star_distribution: Object.fromEntries(counts) });
        this.syncJsonLd({ average, total });
      } catch (error) {
        // the summary is decoration; the list still loads
      }
    }

    renderTotal() { /* the meta line shows the filtered count instead (afterLoad) */ }

    afterLoad(data) {
      const totalEl = this.querySelector('[data-hr-total]');
      if (totalEl) totalEl.textContent = fill(this.labels.results, { n: he(this.total) });
      if (this.page === 1 && this.tab === 'all' && !this.star && !this.query) this.liveJsonLd(data.reviews);
    }

    /** The server's graph carries the admin's numbers; once Yotpo answers, the live ones. */
    syncJsonLd({ average, total }) {
      const script = this.querySelector('[data-rp-jsonld]');
      if (!script) return;
      try {
        const graph = JSON.parse(script.textContent);
        const org = graph['@graph']?.find((node) => node['@id'] === this.dataset.orgId);
        if (!org) return;
        org.aggregateRating = { '@type': 'AggregateRating', ratingValue: (Math.round(average * 10) / 10).toFixed(1), reviewCount: total, bestRating: 5, worstRating: 1 };
        script.textContent = JSON.stringify(graph);
      } catch (error) {
        // malformed graph: leave the server's version
      }
    }

    /** The first page of live reviews as a second ItemList (Google indexes rendered JSON-LD). */
    liveJsonLd(reviews) {
      const orgId = this.dataset.orgId;
      const items = reviews.slice(0, 10).map((review, index) => {
        const product = this.products.get(String(review.product_id));
        const isProduct = product && product.domain_key !== 'yotpo_site_reviews' && product.name;
        const item = {
          '@type': 'Review',
          inLanguage: 'he',
          author: { '@type': 'Person', name: (review.user?.display_name || '').trim() || 'לקוח/ה' },
          datePublished: String(review.created_at || '').slice(0, 10),
          reviewBody: (review.content || review.title || '').trim(),
          reviewRating: { '@type': 'Rating', ratingValue: Number(review.score) || 5, bestRating: 5, worstRating: 1 },
          itemReviewed: isProduct ? { '@type': 'Product', name: product.name, ...(product.product_link ? { url: product.product_link } : {}) } : { '@id': orgId },
          publisher: { '@type': 'Organization', name: 'Yotpo' },
        };
        if (review.title && review.content && review.title !== review.content) item.name = review.title.trim();
        return { '@type': 'ListItem', position: index + 1, item };
      });
      if (!items.length) return;
      let script = this.querySelector('[data-rp-jsonld-live]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-rp-jsonld-live', '');
        this.append(script);
      }
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${location.origin}${location.pathname}#reviews-latest`,
        name: 'הביקורות האחרונות',
        itemListOrder: 'https://schema.org/ItemListOrderDescending',
        numberOfItems: items.length,
        itemListElement: items,
      });
    }

    /** Page extras on a review: its photos and the reviewed product. */
    decorate(node, review) {
      this.renderPhotos(node, review);
      const productEl = node.querySelector('[data-product]');
      const product = this.products.get(String(review.product_id));
      if (productEl && product && product.domain_key !== 'yotpo_site_reviews' && product.name) {
        productEl.hidden = false;
        const link = productEl.querySelector('a');
        link.href = product.product_link || '#';
        link.textContent = product.name;
        const img = productEl.querySelector('img');
        if (product.image_url) { img.src = product.image_url; img.hidden = false; }
      }
    }

  }

  customElements.define('reviews-page', ReviewsPage);

  /* ---------------- product page ---------------- */
  class ProductReviews extends ReviewsList {
    /** One product's reviews from the widget endpoint (bottomline + sub-ratings included). */
    async fetchPage(page) {
      const params = new URLSearchParams({ per_page: this.perPage, page, sort: this.sort, direction: 'desc' });
      if (this.star) params.set('star', this.star);
      return this.request(`${WIDGET_API}/${this.key}/products/${this.dataset.productId}/reviews.json?${params}`);
    }

    renderBottomline(bottomline) {
      super.renderBottomline(bottomline);
      this.renderSubRatings(bottomline?.custom_fields_bottomline);
    }

    /** Yotpo's custom rating fields (איכות השטיח, רוך ונועם…) as label · bar · score. */
    renderSubRatings(fields) {
      const list = this.querySelector('[data-pr-subs]');
      if (!list) return;
      const ratings = Object.values(fields || {}).filter((field) => field.type === 'Rating' && field.title && Number(field.score) > 0);
      list.hidden = !ratings.length;
      list.replaceChildren(...ratings.map((field) => {
        const score = Math.round(Number(field.score) * 10) / 10;
        const dt = document.createElement('dt');
        dt.textContent = field.title;
        const dd = document.createElement('dd');
        dd.innerHTML = '<span class="hr__bar"><span class="hr__bar-fill"></span></span><b></b>';
        dd.querySelector('.hr__bar-fill').style.width = `${(score / 5) * 100}%`;
        dd.querySelector('b').textContent = score.toFixed(1);
        dd.setAttribute('aria-label', `${field.title}: ${fill(this.labels.outOf, { n: score.toFixed(1) })}`);
        const row = document.createElement('div');
        row.className = 'pr__sub';
        row.append(dt, dd);
        return row;
      }));
    }

    /** Product extras on a review: photos and its own custom fields (sub-ratings, room). */
    decorate(node, review) {
      this.renderPhotos(node, review);
      const list = node.querySelector('[data-fields]');
      const fields = Object.values(review.custom_fields || {}).filter((field) => field.title && field.value !== '' && field.value != null);
      if (!list || !fields.length) return;
      list.hidden = false;
      fields.forEach((field) => {
        const li = document.createElement('li');
        li.className = 'pr__field';
        if (field.field_type === 'Rating') {
          const score = Number(field.value);
          if (!(score > 0)) return;
          li.innerHTML = '<span class="pr__field-label"></span><span class="hr__stars" role="img"></span>';
          li.querySelector('.pr__field-label').textContent = field.title;
          const stars = li.querySelector('.hr__stars');
          stars.replaceChildren(...Array.from({ length: 5 }, () => this.starSvg()));
          this.paintStars(stars, score);
          stars.setAttribute('aria-label', `${field.title}: ${fill(this.labels.outOf, { n: score })}`);
        } else {
          li.innerHTML = '<span class="pr__field-label"></span><b></b>';
          li.querySelector('.pr__field-label').textContent = field.title;
          li.querySelector('b').textContent = String(field.value);
        }
        list.append(li);
      });
    }
  }

  customElements.define('product-reviews', ProductReviews);
}
